import { useState } from "react";
import { PageHeader } from "@/pages/Categories";
import { Mail, Phone, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";

const initial = { full_name: "", email: "", company_name: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/messages", form);
      setDone(data.id);
      setForm(initial);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="CONTACT"
        title="Talk to a real human."
        sub="Send a sourcing brief, a question, or just say hello — we typically respond within a working day."
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 border border-white/5 bg-zinc-900/40 rounded-lg p-8 h-fit">
          <div className="font-mono text-[11px] tracking-[0.22em] text-amber-400">DIRECT CHANNELS</div>
          <h2 className="mt-2 font-display text-2xl font-semibold">Get in touch</h2>
          <ul className="mt-6 space-y-4">
            <li className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md border border-white/10 grid place-items-center bg-zinc-950">
                <Mail className="h-4 w-4 text-amber-400" />
              </div>
              <div>
                <div className="text-[12px] text-zinc-500 font-mono">EMAIL</div>
                <a href="mailto:reachus@vyrtrade.com" className="text-[15px] text-zinc-100 hover:text-amber-400" data-testid="contact-email">reachus@vyrtrade.com</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md border border-white/10 grid place-items-center bg-zinc-950">
                <Phone className="h-4 w-4 text-amber-400" />
              </div>
              <div>
                <div className="text-[12px] text-zinc-500 font-mono">PHONE · WHATSAPP</div>
                <a href="https://wa.me/447591440397" target="_blank" rel="noreferrer" className="font-mono text-[15px] text-zinc-100 hover:text-amber-400" data-testid="contact-phone">+44 7591 440397</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md border border-white/10 grid place-items-center bg-zinc-950">
                <MapPin className="h-4 w-4 text-amber-400" />
              </div>
              <div>
                <div className="text-[12px] text-zinc-500 font-mono">OFFICES</div>
                <div className="text-[14px] text-zinc-200">London, UK <span className="text-zinc-500">· Umesh Sridhar</span></div>
                <div className="text-[14px] text-zinc-200">Hyderabad, India <span className="text-zinc-500">· Adithya Menon</span></div>
              </div>
            </li>
          </ul>
          <div className="mt-7 border-t border-white/5 pt-5">
            <div className="text-[12px] text-zinc-500 font-mono">FASTEST PATH</div>
            <p className="mt-2 text-[13.5px] text-zinc-300 leading-relaxed">
              For sourcing, the structured <Link to="/quote" className="text-amber-400 hover:underline">Request Quote</Link> form gets you a vetted shortlist faster.
            </p>
          </div>
        </div>

        <div className="lg:col-span-3">
          {done ? (
            <div className="border border-emerald-500/20 bg-emerald-500/[0.04] rounded-lg p-8" data-testid="contact-success">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                <div className="font-display text-xl font-semibold">Message received</div>
              </div>
              <p className="mt-3 text-zinc-300 text-[14.5px] leading-relaxed">
                Thank you. Your reference is <span className="font-mono text-emerald-300">{done.slice(0, 8).toUpperCase()}</span>. We&apos;ll reply within one working day.
              </p>
              <button onClick={() => setDone(null)} className="mt-5 inline-flex items-center px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm" data-testid="contact-new">
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-zinc-900/40 rounded-lg p-7 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
              <Field label="Full name" required>
                <input data-testid="cf-name" required value={form.full_name} onChange={onChange("full_name")} className={inputCls} placeholder="Jane Doe" />
              </Field>
              <Field label="Work email" required>
                <input data-testid="cf-email" required type="email" value={form.email} onChange={onChange("email")} className={inputCls} placeholder="jane@acme.com" />
              </Field>
              <Field label="Company">
                <input data-testid="cf-company" value={form.company_name} onChange={onChange("company_name")} className={inputCls} placeholder="Acme Ltd" />
              </Field>
              <Field label="Subject">
                <input data-testid="cf-subject" value={form.subject} onChange={onChange("subject")} className={inputCls} placeholder="Partnership / question / sourcing" />
              </Field>
              <Field label="Message" required full>
                <textarea data-testid="cf-message" required rows={6} value={form.message} onChange={onChange("message")} className={inputCls} placeholder="How can we help?" />
              </Field>
              {error && <div className="md:col-span-2 text-sm text-red-400" data-testid="cf-error">{error}</div>}
              <div className="md:col-span-2 flex items-center justify-between gap-4 pt-1">
                <div className="text-[12px] text-zinc-500">By submitting you agree to our <Link to="/privacy" className="text-zinc-300 hover:text-white">Privacy Policy</Link>.</div>
                <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium text-sm disabled:opacity-60" data-testid="cf-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {submitting ? "Sending…" : "Send message"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-zinc-950 border border-white/10 focus:border-amber-400/60 focus:outline-none rounded-md px-3.5 py-2.5 text-[14px] text-zinc-100 placeholder:text-zinc-600 transition-colors";

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[12px] font-mono tracking-wide text-zinc-400 mb-1.5">
        {label}{required ? <span className="text-amber-400 ml-1">*</span> : null}
      </label>
      {children}
    </div>
  );
}
